import { useMemo, useState } from 'react';
import styles from './Calendar.module.css';
import {
  PRESETS, addMonths, iso, monthGrid, parseIso, sameDay,
  type CompareMode, type DateRange, type WindowToken,
} from '@/lib/dateRange';

const DOW = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

/**
 * Date-range picker: presets on the left, two months of calendar on the right.
 *
 * Presets emit a TOKEN rather than computed dates. The API resolves a token
 * against the feed's latest settled day, so "Last 7 Days" lands on days that
 * actually have data — computing the dates client-side against the wall clock
 * is what makes a window come back empty when the crawl is behind.
 */
export function Calendar({
  value,
  maxDate,
  onApply,
  onCancel,
}: {
  value: DateRange;
  /** Latest day with data; later days are not selectable. */
  maxDate?: string;
  onApply: (next: DateRange) => void;
  onCancel: () => void;
}) {
  const max = maxDate ? parseIso(maxDate) : undefined;
  const anchor = value.end ? parseIso(value.end) : (max ?? new Date());

  const [month, setMonth] = useState(() => new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1));
  const [start, setStart] = useState<Date | null>(value.start ? parseIso(value.start) : null);
  const [end, setEnd] = useState<Date | null>(value.end ? parseIso(value.end) : null);
  const [token, setToken] = useState<WindowToken | undefined>(value.token);

  // Comparison period
  const initMode: CompareMode = value.compare_start ? 'custom' : 'auto';
  const [compareMode, setCompareMode] = useState<CompareMode>(initMode);
  const [cmpStart, setCmpStart] = useState<Date | null>(value.compare_start ? parseIso(value.compare_start) : null);
  const [cmpEnd, setCmpEnd] = useState<Date | null>(value.compare_end ? parseIso(value.compare_end) : null);
  const [pickingCompare, setPickingCompare] = useState(false);

  const pickDay = (d: Date): void => {
    if (pickingCompare) {
      // Picking custom comparison dates
      if (!cmpStart || (cmpStart && cmpEnd)) {
        setCmpStart(d);
        setCmpEnd(null);
        return;
      }
      if (d < cmpStart) {
        setCmpEnd(cmpStart);
        setCmpStart(d);
      } else {
        setCmpEnd(d);
        setPickingCompare(false);
      }
      return;
    }
    setToken(undefined);
    if (!start || (start && end)) {
      setStart(d);
      setEnd(null);
      return;
    }
    if (d < start) {
      setEnd(start);
      setStart(d);
    } else {
      setEnd(d);
    }
  };

  const applyPreset = (t: WindowToken): void => {
    setToken(t);
    setStart(null);
    setEnd(null);
    setPickingCompare(false);
  };

  const apply = (): void => {
    const base: DateRange = token ? { token } : (start && end ? { start: iso(start), end: iso(end) } : {});
    if (compareMode === 'custom' && cmpStart && cmpEnd) {
      base.compare_start = iso(cmpStart);
      base.compare_end = iso(cmpEnd);
    } else if (compareMode === 'none') {
      base.compare_start = undefined;
      base.compare_end = undefined;
    }
    // 'auto' and 'last_month' don't set compare fields — server handles them
    onApply(base);
  };

  const onCompareMode = (mode: CompareMode): void => {
    setCompareMode(mode);
    if (mode === 'custom') {
      setPickingCompare(true);
      setCmpStart(null);
      setCmpEnd(null);
    } else {
      setPickingCompare(false);
      setCmpStart(null);
      setCmpEnd(null);
    }
  };

  const months = useMemo(() => [month, addMonths(month, 1)], [month]);
  const canApply = Boolean(token) || Boolean(start && end);
  const nextDisabled = max !== undefined && addMonths(month, 1) > new Date(max.getFullYear(), max.getMonth(), 1);

  const isCmpRange = (d: Date): boolean => Boolean(cmpStart && cmpEnd && d > cmpStart && d < cmpEnd);
  const isCmpEdge = (d: Date): boolean =>
    Boolean((cmpStart && sameDay(d, cmpStart)) || (cmpEnd && sameDay(d, cmpEnd)));

  const inRange = (d: Date): boolean => Boolean(start && end && d > start && d < end);
  const isEdge = (d: Date): boolean =>
    Boolean((start && sameDay(d, start)) || (end && sameDay(d, end)));

  return (
    <div className={styles.picker}>
      <div className={styles.presets}>
        {PRESETS.map((p) => (
          <button
            key={p.token}
            type="button"
            className={`${styles.preset} ${token === p.token ? styles.active : ''}`}
            onClick={() => { applyPreset(p.token); }}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className={styles.body}>
        <div className={styles.months}>
          {months.map((m, mi) => (
            <div className={styles.month} key={m.toISOString()}>
              <div className={styles.monthHead}>
                {mi === 0 ? (
                  <button type="button" className={styles.nav} aria-label="Previous month"
                    onClick={() => { setMonth(addMonths(month, -1)); }}>‹</button>
                ) : <span className={styles.nav} />}
                <span>{m.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</span>
                {mi === 1 ? (
                  <button type="button" className={styles.nav} aria-label="Next month" disabled={nextDisabled}
                    onClick={() => { setMonth(addMonths(month, 1)); }}>›</button>
                ) : <span className={styles.nav} />}
              </div>

              <div className={styles.grid}>
                {DOW.map((d, i) => <span className={styles.dow} key={`${d}${String(i)}`}>{d}</span>)}
                {monthGrid(m).map((d, i) =>
                  d === null ? (
                    <span className={styles.blank} key={`b${String(i)}`} />
                  ) : (
                    <button
                      type="button"
                      key={d.toISOString()}
                      disabled={max !== undefined && d > max}
                      className={`${styles.day} ${inRange(d) ? styles.inRange : ''} ${isEdge(d) ? styles.edge : ''} ${isCmpRange(d) ? styles.cmpRange ?? '' : ''} ${isCmpEdge(d) ? styles.cmpEdge ?? '' : ''}`}
                      onClick={() => { pickDay(d); }}
                    >
                      {d.getDate()}
                    </button>
                  ),
                )}
              </div>
            </div>
          ))}
        </div>

        <div className={styles.footer}>
          <span className={styles.summary}>
            {pickingCompare ? (
              cmpStart && cmpEnd ? (
                <>Compare: <strong>{iso(cmpStart)}</strong> → <strong>{iso(cmpEnd)}</strong></>
              ) : cmpStart ? (
                <>Pick comparison end date</>
              ) : (
                <>Pick comparison start date</>
              )
            ) : token ? (
              <>Preset: <strong>{PRESETS.find((p) => p.token === token)?.label}</strong></>
            ) : start && end ? (
              <><strong>{iso(start)}</strong> → <strong>{iso(end)}</strong></>
            ) : start ? (
              <>Pick an end date</>
            ) : (
              <>Pick a start date, or choose a preset</>
            )}
          </span>
          <span className={styles.actions}>
            <button type="button" className={styles.btn} onClick={onCancel}>Cancel</button>
            <button type="button" className={`${styles.btn} ${styles.primary}`} disabled={!canApply} onClick={apply}>
              Apply
            </button>
          </span>
        </div>

        <div className={styles.footer} style={{ borderTop: '1px solid var(--border, #e0e0e0)', paddingTop: 8 }}>
          <span className={styles.summary} style={{ fontSize: 12 }}>
            <strong>Compare with:</strong>{' '}
            {(['auto', 'custom', 'none'] as const).map((m) => (
              <button
                key={m}
                type="button"
                className={styles.btn}
                style={{
                  fontSize: 11,
                  padding: '2px 8px',
                  marginLeft: 4,
                  fontWeight: compareMode === m ? 700 : 400,
                  textDecoration: compareMode === m ? 'underline' : 'none',
                }}
                onClick={() => { onCompareMode(m); }}
              >
                {m === 'auto' ? 'Auto (previous period)' : m === 'custom' ? 'Custom range' : 'No comparison'}
              </button>
            ))}
          </span>
          {compareMode === 'custom' && cmpStart && cmpEnd && (
            <span style={{ fontSize: 11, color: 'var(--ink-2, #666)' }}>
              vs {iso(cmpStart)} → {iso(cmpEnd)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
